import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Icon from '../../components/AppIcon';

const initialNodes = [
  { id: 1, label: 'Business Strategy', x: 400, y: 280, type: 'root', color: 'bg-primary', textColor: 'text-primary-foreground' },
  { id: 2, label: 'Marketing', x: 180, y: 140, type: 'branch', color: 'bg-blue-500', textColor: 'text-white' },
  { id: 3, label: 'Operations', x: 620, y: 140, type: 'branch', color: 'bg-emerald-500', textColor: 'text-white' },
  { id: 4, label: 'Finance', x: 180, y: 420, type: 'branch', color: 'bg-violet-500', textColor: 'text-white' },
  { id: 5, label: 'Product', x: 620, y: 420, type: 'branch', color: 'bg-amber-500', textColor: 'text-white' },
  { id: 6, label: 'Social Media', x: 40, y: 60, type: 'leaf', color: 'bg-blue-300', textColor: 'text-blue-900' },
  { id: 7, label: 'SEO', x: 40, y: 140, type: 'leaf', color: 'bg-blue-300', textColor: 'text-blue-900' },
  { id: 8, label: 'Content', x: 40, y: 220, type: 'leaf', color: 'bg-blue-300', textColor: 'text-blue-900' },
  { id: 9, label: 'Supply Chain', x: 760, y: 60, type: 'leaf', color: 'bg-emerald-300', textColor: 'text-emerald-900' },
  { id: 10, label: 'HR', x: 760, y: 140, type: 'leaf', color: 'bg-emerald-300', textColor: 'text-emerald-900' },
  { id: 11, label: 'Logistics', x: 760, y: 220, type: 'leaf', color: 'bg-emerald-300', textColor: 'text-emerald-900' },
  { id: 12, label: 'Revenue', x: 40, y: 360, type: 'leaf', color: 'bg-violet-300', textColor: 'text-violet-900' },
  { id: 13, label: 'Budgeting', x: 40, y: 440, type: 'leaf', color: 'bg-violet-300', textColor: 'text-violet-900' },
  { id: 14, label: 'Investment', x: 40, y: 520, type: 'leaf', color: 'bg-violet-300', textColor: 'text-violet-900' },
  { id: 15, label: 'Roadmap', x: 760, y: 360, type: 'leaf', color: 'bg-amber-300', textColor: 'text-amber-900' },
  { id: 16, label: 'Features', x: 760, y: 440, type: 'leaf', color: 'bg-amber-300', textColor: 'text-amber-900' },
  { id: 17, label: 'UX Design', x: 760, y: 520, type: 'leaf', color: 'bg-amber-300', textColor: 'text-amber-900' },
];

const connections = [
  { from: 1, to: 2 }, { from: 1, to: 3 }, { from: 1, to: 4 }, { from: 1, to: 5 },
  { from: 2, to: 6 }, { from: 2, to: 7 }, { from: 2, to: 8 },
  { from: 3, to: 9 }, { from: 3, to: 10 }, { from: 3, to: 11 },
  { from: 4, to: 12 }, { from: 4, to: 13 }, { from: 4, to: 14 },
  { from: 5, to: 15 }, { from: 5, to: 16 }, { from: 5, to: 17 },
];

const templates = [
  { id: 'business', label: 'Business Strategy', icon: 'Briefcase', description: 'Map your business goals and strategies' },
  { id: 'project', label: 'Project Planning', icon: 'FolderOpen', description: 'Break down project tasks and milestones' },
  { id: 'brainstorm', label: 'Brainstorming', icon: 'Lightbulb', description: 'Capture and organize creative ideas' },
  { id: 'swot', label: 'SWOT Analysis', icon: 'BarChart2', description: 'Analyze strengths, weaknesses, opportunities, threats' },
];

const MindMapping = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [nodes, setNodes] = useState(initialNodes);
  const [activeTemplate, setActiveTemplate] = useState('business');
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [newNodeLabel, setNewNodeLabel] = useState('');

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setEditingLabel(node?.label);
    setIsEditing(false);
  };

  const handleSaveLabel = () => {
    if (editingLabel?.trim()) {
      setNodes(prev => prev?.map(n => n?.id === selectedNode?.id ? { ...n, label: editingLabel } : n));
      setSelectedNode(prev => ({ ...prev, label: editingLabel }));
    }
    setIsEditing(false);
  };

  const handleAddNode = () => {
    if (!newNodeLabel?.trim()) return;
    const newNode = {
      id: nodes?.length + 1,
      label: newNodeLabel,
      x: 400 + Math.random() * 200 - 100,
      y: 560 + Math.random() * 60,
      type: 'leaf',
      color: 'bg-slate-400',
      textColor: 'text-white',
    };
    setNodes(prev => [...prev, newNode]);
    setNewNodeLabel('');
    setShowAddNodeModal(false);
  };

  const handleDeleteNode = (nodeId) => {
    if (nodes?.find(n => n?.id === nodeId)?.type === 'root') return;
    setNodes(prev => prev?.filter(n => n?.id !== nodeId));
    setSelectedNode(null);
  };

  const getNodeById = (id) => nodes?.find(n => n?.id === id);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <div
        className={`flex flex-col flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <AuthHeader
          onMenuToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isMenuCollapsed={isSidebarCollapsed}
        />
        <main className="flex-1 overflow-y-auto p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Mind Mapping</h1>
              <p className="text-sm text-muted-foreground font-body mt-1">
                Visually organize ideas, strategies, and business concepts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAddNodeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={16} color="currentColor" />
                Add Node
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-colors">
                <Icon name="Download" size={16} color="currentColor" />
                Export
              </button>
            </div>
          </div>

          {/* Templates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {templates?.map((tpl) => (
              <button
                key={tpl?.id}
                onClick={() => setActiveTemplate(tpl?.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                  activeTemplate === tpl?.id
                    ? 'border-primary bg-primary/10' :'border-border bg-card hover:bg-muted'
                }`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                  activeTemplate === tpl?.id ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  <Icon name={tpl?.icon} size={16} color={activeTemplate === tpl?.id ? 'var(--color-primary)' : 'currentColor'} />
                </div>
                <div>
                  <p className={`text-xs font-semibold font-body ${activeTemplate === tpl?.id ? 'text-primary' : 'text-foreground'}`}>
                    {tpl?.label}
                  </p>
                  <p className="text-xs text-muted-foreground hidden md:block">{tpl?.description}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="flex gap-6">
            {/* Mind Map Canvas */}
            <div className="flex-1 bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="text-sm font-semibold text-foreground font-body">Canvas</span>
                <div className="flex items-center gap-2">
                  <button className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom In">
                    <Icon name="ZoomIn" size={16} color="currentColor" />
                  </button>
                  <button className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom Out">
                    <Icon name="ZoomOut" size={16} color="currentColor" />
                  </button>
                  <button className="p-1.5 rounded hover:bg-muted transition-colors" title="Reset View">
                    <Icon name="Maximize2" size={16} color="currentColor" />
                  </button>
                </div>
              </div>
              <div className="relative overflow-auto" style={{ height: '520px' }}>
                <svg
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: '900px', height: '620px' }}
                >
                  {connections?.map((conn, idx) => {
                    const fromNode = getNodeById(conn?.from);
                    const toNode = getNodeById(conn?.to);
                    if (!fromNode || !toNode) return null;
                    const fx = fromNode?.x + 60;
                    const fy = fromNode?.y + 18;
                    const tx = toNode?.x + 60;
                    const ty = toNode?.y + 18;
                    return (
                      <line
                        key={idx}
                        x1={fx} y1={fy}
                        x2={tx} y2={ty}
                        stroke="var(--color-border)"
                        strokeWidth="1.5"
                        strokeDasharray={toNode?.type === 'leaf' ? '4 3' : '0'}
                      />
                    );
                  })}
                </svg>
                <div className="relative" style={{ width: '900px', height: '620px' }}>
                  {nodes?.map((node) => (
                    <button
                      key={node?.id}
                      onClick={() => handleNodeClick(node)}
                      style={{ position: 'absolute', left: node?.x, top: node?.y }}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold font-body shadow-sm transition-all border-2 min-w-[80px] text-center ${node?.color} ${node?.textColor} ${
                        selectedNode?.id === node?.id
                          ? 'border-foreground scale-105 shadow-md'
                          : 'border-transparent hover:scale-105 hover:shadow-md'
                      } ${node?.type === 'root' ? 'text-sm px-5 py-3 rounded-xl' : ''}`}
                    >
                      {node?.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Node Properties Panel */}
            <div className="w-64 flex-shrink-0 space-y-4">
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-foreground font-body mb-3 flex items-center gap-2">
                  <Icon name="Settings2" size={15} color="var(--color-primary)" />
                  Node Properties
                </h3>
                {selectedNode ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-muted-foreground font-caption block mb-1">Label</label>
                      {isEditing ? (
                        <div className="flex gap-1">
                          <input
                            className="flex-1 text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e?.target?.value)}
                            onKeyDown={(e) => e?.key === 'Enter' && handleSaveLabel()}
                            autoFocus
                          />
                          <button
                            onClick={handleSaveLabel}
                            className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs"
                          >
                            ✓
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-foreground font-body">{selectedNode?.label}</span>
                          <button
                            onClick={() => setIsEditing(true)}
                            className="p-1 hover:bg-muted rounded transition-colors"
                          >
                            <Icon name="Pencil" size={12} color="currentColor" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-caption block mb-1">Type</label>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${
                        selectedNode?.type === 'root' ? 'bg-primary/20 text-primary' :
                        selectedNode?.type === 'branch'? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground'
                      }`}>
                        {selectedNode?.type}
                      </span>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-caption block mb-1">Position</label>
                      <p className="text-xs text-foreground font-body">X: {Math.round(selectedNode?.x)}, Y: {Math.round(selectedNode?.y)}</p>
                    </div>
                    {selectedNode?.type !== 'root' && (
                      <button
                        onClick={() => handleDeleteNode(selectedNode?.id)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-destructive/10 text-destructive rounded-md text-xs font-medium hover:bg-destructive/20 transition-colors"
                      >
                        <Icon name="Trash2" size={13} color="currentColor" />
                        Delete Node
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground font-caption">Click a node to view and edit its properties.</p>
                )}
              </div>

              {/* Map Stats */}
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-foreground font-body mb-3 flex items-center gap-2">
                  <Icon name="BarChart3" size={15} color="var(--color-primary)" />
                  Map Stats
                </h3>
                <div className="space-y-2">
                  {[
                    { label: 'Total Nodes', value: nodes?.length, icon: 'Circle' },
                    { label: 'Branches', value: nodes?.filter(n => n?.type === 'branch')?.length, icon: 'GitBranch' },
                    { label: 'Leaf Nodes', value: nodes?.filter(n => n?.type === 'leaf')?.length, icon: 'Leaf' },
                    { label: 'Connections', value: connections?.length, icon: 'Link' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name={stat?.icon} size={13} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground font-caption">{stat?.label}</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground font-body">{stat?.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-primary font-body mb-2 flex items-center gap-2">
                  <Icon name="Lightbulb" size={13} color="var(--color-primary)" />
                  Tips
                </h3>
                <ul className="space-y-1.5">
                  {['Click a node to select and edit it', 'Use Add Node to expand your map', 'Export your map as an image or PDF']?.map((tip, i) => (
                    <li key={i} className="text-xs text-muted-foreground font-caption flex items-start gap-1.5">
                      <span className="text-primary mt-0.5">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Add Node Modal */}
      {showAddNodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 w-80 shadow-xl">
            <h3 className="text-base font-semibold text-foreground font-heading mb-4">Add New Node</h3>
            <div className="mb-4">
              <label className="text-xs text-muted-foreground font-caption block mb-1">Node Label</label>
              <input
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter node label..."
                value={newNodeLabel}
                onChange={(e) => setNewNodeLabel(e?.target?.value)}
                onKeyDown={(e) => e?.key === 'Enter' && handleAddNode()}
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowAddNodeModal(false); setNewNodeLabel(''); }}
                className="flex-1 px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNode}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Add Node
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MindMapping;
